/**
 * OpenAI GPT Image 2 family — the catalogue entries that all run through the
 * same `openai.images.generate|edit` code path (`generateGptImage2Image` /
 * `editImageWithGptImage2` in `@/utils/images`), differing only in which
 * OpenAI model id they hit and which `quality` they request.
 *
 * Pure data, no imports: this is consumed by client bundles (pickers, credit
 * estimators) AND by the server helpers, so keep it dependency-free.
 *
 * Pricing: GPT Image 2.5 Flare/Sunburst (2026-09-08) share GPT Image 2's
 * per-token rates ($5/M text in, $8/M image in, $30/M image out), so all
 * tiers bill the same credits as their GPT Image 2 counterpart. Verify the
 * per-image token usage on the first live run — the per-token rate is equal,
 * but a model that spends more image-output tokens per picture would still
 * cost more per image.
 */
export type GptImage2Quality = 'low' | 'medium' | 'high';

export type GptImage2OpenAiModel =
  | 'gpt-image-2'
  | 'gpt-image-2.5-flare'
  | 'gpt-image-2.5-sunburst';

export interface GptImage2FamilyEntry {
  openaiModel: GptImage2OpenAiModel;
  quality: GptImage2Quality;
}

export const GPT_IMAGE_2_FAMILY: Record<string, GptImage2FamilyEntry> = {
  'OpenAI GPT Image 2 Low': { openaiModel: 'gpt-image-2', quality: 'low' },
  'OpenAI GPT Image 2 Medium': {
    openaiModel: 'gpt-image-2',
    quality: 'medium',
  },
  'OpenAI GPT Image 2 High': { openaiModel: 'gpt-image-2', quality: 'high' },
  'OpenAI GPT Image 2.5 Flare Low': {
    openaiModel: 'gpt-image-2.5-flare',
    quality: 'low',
  },
  'OpenAI GPT Image 2.5 Flare Medium': {
    openaiModel: 'gpt-image-2.5-flare',
    quality: 'medium',
  },
  'OpenAI GPT Image 2.5 Flare High': {
    openaiModel: 'gpt-image-2.5-flare',
    quality: 'high',
  },
  'OpenAI GPT Image 2.5 Sunburst Medium': {
    openaiModel: 'gpt-image-2.5-sunburst',
    quality: 'medium',
  },
  'OpenAI GPT Image 2.5 Sunburst High': {
    openaiModel: 'gpt-image-2.5-sunburst',
    quality: 'high',
  },
};

export const GPT_IMAGE_2_FAMILY_MODELS = Object.keys(GPT_IMAGE_2_FAMILY);

/**
 * The platform's default image model — what Marky's image tools, the chat
 * image-model picker, the Image Generator and its edit modals preselect when
 * the user has not chosen anything. Swapped from GPT Image 2 Medium to 2.5
 * Flare Medium on 2026-09-13 (same 3-credit tier, ~4x fewer output tokens,
 * 2-3x faster, Arena-ranked above GPT Image 2 medium). Credits deliberately
 * left unchanged. Product pins that are NOT this default (360° panorama,
 * Photo Studio, RoomGPT, headshots, photo packs, slides' OpenAI engine, preset
 * worksheets/carousels, media/workflow templates) keep their own model.
 */
export const DEFAULT_GPT_IMAGE_MODEL = 'OpenAI GPT Image 2.5 Flare Medium';

/**
 * Previous default. Both the chat picker and the Image Generator persist the
 * CURRENT selection to localStorage on every load, so a stored value equal
 * to the old default is almost always "never chose anything" rather than a
 * deliberate pick — readers map it forward so the swap reaches existing
 * users. A user who really wanted GPT Image 2 Medium picks it again once.
 */
export const LEGACY_DEFAULT_GPT_IMAGE_MODEL = 'OpenAI GPT Image 2 Medium';

/**
 * The GPT model for LONG, STRUCTURED prompts that must render specific text —
 * image-mode slides and the text-heavy presets (carousels, visual worksheet,
 * birthday card). Sunburst costs exactly what Flare costs (same per-token
 * rates, identical measured token usage) but follows a 1,500-word brief more
 * faithfully: in a controlled rerun of a title-slide prompt it rendered the
 * requested body paragraph 2/2 times where Flare dropped it 2/2 times
 * (2026-09-13). It is ~25-40% slower, which is why it is NOT the platform
 * default for interactive single-image use.
 */
export const STRUCTURED_GPT_IMAGE_MODEL =
  'OpenAI GPT Image 2.5 Sunburst Medium';
/** 4K / high-quality tier of the structured-prompt model. */
export const STRUCTURED_GPT_IMAGE_MODEL_HIGH =
  'OpenAI GPT Image 2.5 Sunburst High';

export const GPT_IMAGE_2_CREDITS_BY_QUALITY: Record<GptImage2Quality, number> =
  {
    low: 0.5,
    medium: 3,
    high: 11,
  };

/** True for every GPT Image 2 / 2.5 catalogue name (generation AND edit). */
export const isGptImage2FamilyModel = (
  model: string | null | undefined,
): boolean =>
  !!model && Object.prototype.hasOwnProperty.call(GPT_IMAGE_2_FAMILY, model);

/** Per-image credits for a family member; undefined for anything else. */
export const getGptImage2FamilyCredits = (
  model: string | null | undefined,
): number | undefined => {
  const entry = model ? GPT_IMAGE_2_FAMILY[model] : undefined;
  return entry ? GPT_IMAGE_2_CREDITS_BY_QUALITY[entry.quality] : undefined;
};

/** Aspect ratios accepted by the family's text-to-image branch. */
export const GPT_IMAGE_2_ASPECTS = ['1:1', '2:3', '3:2', '16:9', '9:16'];

/** Aspect ratios accepted by the family's edit branch (`auto` = follow input). */
export const GPT_IMAGE_2_EDIT_ASPECTS = ['auto', ...GPT_IMAGE_2_ASPECTS];

/**
 * OpenAI `size` per aspect ratio. gpt-image-2 (and the 2.5 models, which share
 * its request shape) take custom sizes: multiples of 16, max edge 3840px, max
 * aspect ratio 3:1, 655,360–8,294,400 total pixels. Each aspect gets an
 * exact-ratio size inside that envelope rather than the legacy fixed sizes.
 * '2:1' is the 360° panorama size.
 * Docs: https://developers.openai.com/api/docs/models/gpt-image-2
 */
export const GPT_IMAGE_2_ASPECT_TO_SIZE: Record<string, string> = {
  '1:1': '1024x1024',
  '2:3': '1024x1536',
  '3:2': '1536x1024',
  '4:5': '1024x1280',
  '16:9': '1792x1008',
  '9:16': '1008x1792',
  '2:1': '3840x1920',
};

// A pixel size or an off-table ratio is matched against every aspect except
// the panorama's '2:1': at 3840x1920 it has 4x+ the pixels of any other entry,
// so a banner-shaped request would silently become a far bigger, slower (and
// likely costlier) render. Asking for '2:1' by name still gets it.
const GPT_IMAGE_2_NEAREST_ASPECTS = Object.keys(
  GPT_IMAGE_2_ASPECT_TO_SIZE,
).filter((aspect) => aspect !== '2:1');

/**
 * Resolves a `dimensions` value to the aspect ratio a GPT Image call renders
 * and the OpenAI `size` for it:
 * - an aspect in GPT_IMAGE_2_ASPECT_TO_SIZE passes through;
 * - a pixel size ('1792x1024', the format /api/generate-image and the MCP
 *   generate_image tool document) or another ratio ('4:3') maps to the
 *   nearest aspect, compared in log space so the pick is the one that needs
 *   the least cropping. The old aspect-only lookup rendered every pixel size
 *   as a 1024x1024 square;
 * - 'auto', 'match_input_image' or nothing: an edit follows its input image
 *   (`size: 'auto'`), text-to-image has nothing to follow and renders 1:1;
 * - anything else renders 1:1.
 */
export const resolveGptImage2Size = (
  dimensions: string | null | undefined,
  { edit = false }: { edit?: boolean } = {},
): { aspect: string; size: string } => {
  const value = typeof dimensions === 'string' ? dimensions.trim() : '';
  const square = { aspect: '1:1', size: GPT_IMAGE_2_ASPECT_TO_SIZE['1:1'] };

  if (!value || value === 'auto' || value === 'match_input_image') {
    return edit ? { aspect: 'auto', size: 'auto' } : square;
  }
  if (Object.prototype.hasOwnProperty.call(GPT_IMAGE_2_ASPECT_TO_SIZE, value)) {
    return { aspect: value, size: GPT_IMAGE_2_ASPECT_TO_SIZE[value] };
  }

  const match = /^(\d+(?:\.\d+)?)\s*[x:]\s*(\d+(?:\.\d+)?)$/i.exec(value);
  const ratio = match ? Number(match[1]) / Number(match[2]) : NaN;
  if (!Number.isFinite(ratio) || ratio <= 0) return square;

  let aspect = '1:1';
  let bestDistance = Infinity;
  for (const candidate of GPT_IMAGE_2_NEAREST_ASPECTS) {
    const [width, height] = candidate.split(':').map(Number);
    const distance = Math.abs(Math.log(width / height / ratio));
    if (distance < bestDistance) {
      aspect = candidate;
      bestDistance = distance;
    }
  }
  return { aspect, size: GPT_IMAGE_2_ASPECT_TO_SIZE[aspect] };
};
